import { createContext } from "preact";
import { useContext, useState, useEffect, useRef } from "preact/hooks";
import type { ComponentChildren } from "preact";
import { useWidget } from "./WidgetProvider";
import { useAppMessage, useParticipantIds } from "@daily-co/daily-react";
import { useUserInteraction } from "./UserInteractionProvider";

interface MessageContextType {
  lastMessage: string;
  setLastMessage: (message: string) => void;
  queuedMessage: string;
  setQueuedMessage: (message: string) => void;
  resetInitMsg: () => void;
}

const MessageContext = createContext<MessageContextType | undefined>(undefined);

interface MessageProviderProps {
  children: ComponentChildren;
}

export function MessageProvider({ children }: MessageProviderProps) {
  const [lastMessage, setLastMessage] = useState<string>("");
  const [queuedMessage, setQueuedMessage] = useState<string>("");
  const { hasJoinedRoom } = useWidget();
  const { hasUserInteracted } = useUserInteraction();
  const sendAppMessage = useAppMessage();
  const remoteParticipantIds = useParticipantIds({ filter: "remote" });
  const hasSentInitMsgRef = useRef<boolean>(false);

  const DEFAULT_INIT_MSG = "hello";

  const resetInitMsg = () => {
    hasSentInitMsgRef.current = false;
  };

  // Send queued message when call is ready
  useEffect(() => {
    const isCallReady = remoteParticipantIds.length > 0 && hasJoinedRoom && hasUserInteracted;
    if (isCallReady && !hasSentInitMsgRef.current) {
      hasSentInitMsgRef.current = true;
      let msg: string;
      if (queuedMessage) {
        setLastMessage(queuedMessage);
        msg = queuedMessage;
      } else {
        msg = DEFAULT_INIT_MSG;
      }
      sendAppMessage(
        {
          event: "chat-msg",
          message: msg,
          name: "Guest",
        },
        "*"
      );
      setQueuedMessage("");
    }
  }, [queuedMessage, hasJoinedRoom, remoteParticipantIds, hasUserInteracted, hasSentInitMsgRef]);

  return <MessageContext.Provider value={{ lastMessage, setLastMessage, queuedMessage, setQueuedMessage, resetInitMsg }}>{children}</MessageContext.Provider>;
}

export function useMessage(): MessageContextType {
  const context = useContext(MessageContext);
  if (!context) {
    throw new Error("useMessage must be used within a MessageProvider");
  }
  return context;
}
